import { loadStripe, StripeEmbeddedCheckout } from "@stripe/stripe-js";
import { ref, Ref } from "vue";
import { makeApi } from "~/api";
import { Currency } from "fungi-types";
import { auctionApi } from "~/api/auction";

const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string;

if (!stripePublishableKey) {
  throw new Error("Stripe publishable key is not set");
}

const api = makeApi(`${import.meta.env.VITE_API_URL}/payments/stripe`);

import { defineStore } from "pinia";
import { Scarcity } from "fungi-types";

export const useStripeStore = defineStore("stripe", () => {
  const stripeLoader = loadStripe(stripePublishableKey);
  const checkoutSessionInProgress = ref(false);
  const checkoutSession = ref<StripeEmbeddedCheckout | null>(null);
  const checkoutSessionId = ref<string>("");

  async function fetchClientSecret({
    auctionCuid,
    packType,
    amountInCents,
    currency,
    creditAmount = 0,
  }: {
    auctionCuid?: string;
    packType?: Scarcity;
    amountInCents: number;
    currency: Currency;
    creditAmount: number;
  }) {
    const checkoutSessionData = await auctionApi.stripe.checkoutSession.create.mutate({
      currency,
      auctionCuid,
      packType,
      amount: amountInCents,
      creditAmount,
    });

    return checkoutSessionData;
  }

  async function buyPack() {
    const input = {
      sessionId: checkoutSessionId.value,
    };
    const job = await auctionApi.pack.buy.stripe.mutate(input);
    return job;
  }

  async function launchCheckoutSession({
    ref,
    auctionCuid,
    packType,
    amountInCents,
    currency,
    successCallback,
    errorCallback,
    creditAmount = 0,
  }: {
    ref: Ref<HTMLElement>;
    auctionCuid?: string;
    packType?: Scarcity;
    amountInCents: number;
    currency: Currency;
    creditAmount;
    successCallback?: () => unknown;
    errorCallback?: () => unknown;
  }) {
    checkoutSessionInProgress.value = false;

    const stripe = await stripeLoader;
    if (!stripe) {
      console.error("Failed to load Stripe");
      return;
    }

    checkoutSession.value = await stripe.initEmbeddedCheckout({
      fetchClientSecret: async () => {
        try {
          const { clientSecret, sessionId } = await fetchClientSecret({
            auctionCuid,
            packType,
            amountInCents,
            currency,
            creditAmount,
          });
          checkoutSessionId.value = sessionId;
          checkoutSessionInProgress.value = true;
          return clientSecret;
        } catch (error) {
          console.error("Something went wrong while fetching stripe client secret", error);

          checkoutSessionInProgress.value = false;

          checkoutSession.value?.destroy();

          errorCallback?.();

          throw error;
        }
      },
      onComplete: async () => {
        checkoutSession.value?.destroy();

        checkoutSessionInProgress.value = false;

        successCallback?.();

        checkoutSession.value = null;
      },
    });

    checkoutSession.value?.mount(ref.value);

    return checkoutSession;
  }

  function cancelCheckoutSession() {
    checkoutSession.value?.destroy();
    checkoutSession.value = null;
    checkoutSessionInProgress.value = false;
  }

  return {
    launchCheckoutSession,
    fetchClientSecret,
    checkoutSessionId,
    checkoutSessionInProgress,
    checkoutSession,
    buyPack,
    cancelCheckoutSession,
  };
});
